import React, { useState } from 'react';
import {
    Headphones,
    Phone,
    Mail,
    MapPin,
    Globe,
    Send,
    Lock,
    ArrowRight,
    ChevronDown,
    Bot,
    MessageCircle
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';

export default function ContactPage() {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Logique de soumission
    };

    return (
        <PublicPageLayout>
            <div className="min-h-screen bg-[#FBFDFF] py-12 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 space-y-12">

                    {/* ================= SECTION SUPERIEURE : COORDONNÉES + FORMULAIRE ================= */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                        {/* COLONNE GAUCHE : TITRE & BLOCS CONTACT */}
                        <div className="lg:col-span-6 space-y-8">
                            {/* En-tête */}
                            <div className="space-y-3">
                                <span className="text-xs font-bold text-blue-600 tracking-wider uppercase">
                                    CONTACT
                                </span>
                                <h1 className="text-3xl lg:text-[40px] font-extrabold text-slate-900 leading-[1.15]">
                                    Parlons de votre projet.<br />
                                    Nous sommes là pour <span className="text-blue-600">vous aider.</span>
                                </h1>
                                <p className="text-slate-500 text-xs leading-relaxed max-w-md pt-1">
                                    Une question, une demande de démonstration ou un besoin spécifique ?<br />
                                    Notre équipe vous répond dans les plus brefs délais.
                                </p>
                            </div>

                            {/* Grille 2x2 des canaux de contact */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                                {/* Support 24/7 */}
                                <div className="bg-white rounded-2xl p-4 border border-slate-200/70 shadow-sm flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                        <Headphones size={20} />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-xs font-extrabold text-slate-900">Support 24/7</h3>
                                        <p className="text-[11px] text-slate-500 leading-tight">
                                            Notre équipe support est disponible 24h/24 et 7j/7.
                                        </p>
                                        <a href="mailto:support@izicallcenter.com" className="text-[11px] font-bold text-blue-600 block pt-0.5">
                                            support@izicallcenter.com
                                        </a>
                                    </div>
                                </div>

                                {/* Téléphone */}
                                <div className="bg-white rounded-2xl p-4 border border-slate-200/70 shadow-sm flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                                        <Phone size={20} />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-xs font-extrabold text-slate-900">Téléphone</h3>
                                        <p className="text-[11px] text-slate-500 leading-tight">
                                            Du lundi au vendredi<br />8h00 - 18h00 (GMT)
                                        </p>
                                        <a href="tel:+14388881020" className="text-[11px] font-bold text-blue-600 block pt-0.5">
                                            +1 (438) 888-1020
                                        </a>
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="bg-white rounded-2xl p-4 border border-slate-200/70 shadow-sm flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
                                        <Mail size={20} />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-xs font-extrabold text-slate-900">Email</h3>
                                        <p className="text-[11px] text-slate-500 leading-tight">
                                            Écrivez-nous à tout moment.
                                        </p>
                                        <a href="mailto:contact@izicallcenter.com" className="text-[11px] font-bold text-blue-600 block pt-1">
                                            contact@izicallcenter.com
                                        </a>
                                    </div>
                                </div>

                                {/* Adresse */}
                                <div className="bg-white rounded-2xl p-4 border border-slate-200/70 shadow-sm flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                                        <MapPin size={20} />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-xs font-extrabold text-slate-900">Adresse</h3>
                                        <p className="text-[11px] text-slate-500 leading-tight">
                                            1320, 15th Street SE, Suite 502<br />Calgary, AB T2G 3N3, Canada
                                        </p>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* COLONNE DROITE : FORMULAIRE DE CONTACT */}
                        <div className="lg:col-span-5 bg-white rounded-3xl px-6 py-4 border border-slate-200/70 shadow-sm">
                            <div className="mb-3">
                                <h2 className="text-xl font-extrabold text-slate-900">Envoyez-nous un message</h2>
                                <p className="text-xs text-slate-500 mt-1">
                                    Remplissez le formulaire et nous vous répondrons rapidement.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-2.5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {/* Nom complet */}
                                    <div className="space-y-0.5">
                                        <label className="text-[11px] font-bold text-slate-700">
                                            Nom complet <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleChange}
                                            placeholder="Votre nom"
                                            className="w-full px-3.5 py-2 rounded-xl border border-slate-200 bg-slate-50/50 text-xs text-slate-800 focus:outline-none focus:border-blue-600 focus:bg-white transition-all"
                                            required
                                        />
                                    </div>

                                    {/* Email professionnel */}
                                    <div className="space-y-0.5">
                                        <label className="text-[11px] font-bold text-slate-700">
                                            Email professionnel <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="nom@entreprise.com"
                                            className="w-full px-3.5 py-2 rounded-xl border border-slate-200 bg-slate-50/50 text-xs text-slate-800 focus:outline-none focus:border-blue-600 focus:bg-white transition-all"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {/* Entreprise */}
                                    <div className="space-y-0.5">
                                        <label className="text-[11px] font-bold text-slate-700">Entreprise</label>
                                        <input
                                            type="text"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleChange}
                                            placeholder="Nom de votre entreprise"
                                            className="w-full px-3.5 py-2 rounded-xl border border-slate-200 bg-slate-50/50 text-xs text-slate-800 focus:outline-none focus:border-blue-600 focus:bg-white transition-all"
                                        />
                                    </div>

                                    {/* Téléphone */}
                                    <div className="space-y-0.5">
                                        <label className="text-[11px] font-bold text-slate-700">Téléphone</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="+1 (000) 000-0000"
                                            className="w-full px-3.5 py-2 rounded-xl border border-slate-200 bg-slate-50/50 text-xs text-slate-800 focus:outline-none focus:border-blue-600 focus:bg-white transition-all"
                                        />
                                    </div>
                                </div>

                                {/* Sujet */}
                                <div className="space-y-0.5">
                                    <label className="text-[11px] font-bold text-slate-700">
                                        Sujet <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            className="w-full px-3.5 py-2 rounded-xl border border-slate-200 bg-slate-50/50 text-xs text-slate-700 focus:outline-none focus:border-blue-600 focus:bg-white transition-all appearance-none cursor-pointer"
                                            required
                                        >
                                            <option value="" disabled>Sélectionnez un sujet</option>
                                            <option value="demo">Demande de démonstration</option>
                                            <option value="support">Support technique</option>
                                            <option value="commercial">Information commerciale</option>
                                            <option value="other">Autre</option>
                                        </select>
                                        <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="space-y-0.5">
                                    <label className="text-[11px] font-bold text-slate-700">
                                        Votre message <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="message"
                                        rows={3}
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Décrivez votre besoin ou votre projet..."
                                        className="w-full px-3.5 py-2 rounded-xl border border-slate-200 bg-slate-50/50 text-xs text-slate-800 focus:outline-none focus:border-blue-600 focus:bg-white transition-all resize-none"
                                        required
                                    />
                                </div>

                                {/* Bouton de soumission */}
                                <button
                                    type="submit"
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2 shadow-md shadow-blue-500/20 mt-1"
                                >
                                    <Send size={14} />
                                    <span>Envoyer le message</span>
                                </button>

                                {/* Indication de sécurité */}
                                <div className="flex items-center justify-center gap-1.5 pt-1 text-slate-400 text-[10px]">
                                    <Lock size={12} />
                                    <span>Vos informations sont sécurisées et ne seront jamais partagées.</span>
                                </div>
                            </form>
                        </div>

                        {/* COLONNE DROITE EXTRÊME : WIDGET ASSISTANT IA */}
                        <div className="lg:col-span-1 flex flex-col items-center  justify-center space-y-3 h-full pt-4 lg:pt-0">
                            {/* Bulle d'aide IA */}
                            <div className="bg-white rounded-2xl p-2.5 border border-slate-200/80 shadow-lg flex items-center gap-3 w-64 lg:w-36 group cursor-pointer hover:shadow-xl transition-all">
                                <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                    <Bot size={18} />
                                </div>
                                <div className="flex-0.2 min-w-0">
                                    <h4 className="text-[8px] font-bold text-slate-900 truncate">Besoin d'aide ?</h4>
                                    <p className="text-[6px] text-slate-500 leading-tight line-clamp-2">Discutez avec Gaston</p>
                                </div>
                                <ArrowRight size={12} className="text-slate-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                            </div>

                            {/* Bouton rond bleu chat */}
                            <button className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-600/30 hover:bg-blue-700 hover:scale-105 transition-all shrink-0">
                                <MessageCircle size={22} />
                            </button>
                        </div>

                    </div>

                    {/* ================= SECTION INFERIEURE : PRÉSENCE MONDIALE ================= */}
                    <div className="bg-[#f4f8ff] rounded-3xl border border-blue-100/60 overflow-hidden grid grid-cols-1 lg:grid-cols-12 items-center">

                        {/* Texte Gauche */}
                        <div className="lg:col-span-4 p-8 lg:p-10 space-y-4">
                            {/* En-tête avec Icône + Titre côte à côte */}
                            <div className="flex items-center gap-3.5">
                                <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/20">
                                    <Globe size={22} />
                                </div>
                                <h3 className="text-base lg:text-lg font-extrabold text-slate-900 leading-snug">
                                    Une présence mondiale, un service local.
                                </h3>
                            </div>

                            {/* Paragraphe descriptif */}
                            <p className="text-slate-500 text-xs leading-relaxed pl-0.5">
                                IZI Call Center accompagne des entreprises dans plus de <strong className="text-slate-700">20 pays</strong> grâce à une infrastructure cloud sécurisée et performante.
                            </p>

                            {/* Lien */}
                            <a href="#installations" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors pt-1">
                                <span>Voir nos implantations</span>
                                <ArrowRight size={14} />
                            </a>
                        </div>

                        {/* Illustration Carte du monde */}
                        <div className="lg:col-span-8 p-4 lg:p-6 flex justify-center items-center">
                            <img
                                src="/contact/world-map.png"
                                alt="Carte de présence mondiale IZI Call Center"
                                className="w-full h-auto max-h-72 object-contain mix-blend-multiply"
                            />
                        </div>

                    </div>

                </div>
            </div>
        </PublicPageLayout>
    );
}